import {
    Dialog,
    Divider,
    DialogContent,
    Grid,
    DialogActions,
    Button,
} from "@mui/material"
import BaseDialogTitle from "src/components/dialog/BaseDialogTitle"
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import {useForm, useWatch} from "react-hook-form";
import {useCallback, useEffect, useState} from "react";
import { ControlledTextField } from "src/components/forms";
import {NotRequiredSchema, RequiredSchema} from "../../../../../utils/validations";
import {
    ClientAddress,
    ClientAddressFields,
    ClientAddressTypes,
    Department,
    Municipality, Province
} from "../../../../../types/client/clientData";
import {LoaderBlockUI} from "../../../../../components/loader";
import {HttpClientAddress} from "../../../../../http/client/httpClient";
import {removeDuplicatesById} from "../../../../../utils/helpers";
import {HttpClientAddressData} from "../../../../../http/client/httpClientAddressData";

interface DialogAddressProps {
    open: boolean,
    setEditAddress: (value: boolean) => void,
    renderAddressTitle: () => string,
    address?: ClientAddress,
    onClose: () => void,
    onSubmitDialog: () => void,
    clientId: number,
    addressType?: ClientAddressTypes
}


export type AddressFormData = {
    [ClientAddressFields.Id]?: number,
    [ClientAddressFields.AddressTypeCode]: ClientAddressTypes | undefined,
    [ClientAddressFields.Street]: string | null,
    [ClientAddressFields.StreetNumber]: string | null,
    [ClientAddressFields.Floor]: string | null,
    [ClientAddressFields.Apartment]: string | null,
    [ClientAddressFields.CountryCode]: number,
    [ClientAddressFields.PostalCode]: string | null,
    [ClientAddressFields.ProvinceCode]: number | null,
    [ClientAddressFields.DepartmentCode]: number | null,
    [ClientAddressFields.MunicipalityCode]: number | null,
    [ClientAddressFields.Verified]: boolean,
}

const DialogAddress = (props: DialogAddressProps) => {
    const AddressFormSchema = yup.object().shape({
        [ClientAddressFields.AddressTypeCode]: NotRequiredSchema,
        [ClientAddressFields.PostalCode]: RequiredSchema,
        [ClientAddressFields.Street]: RequiredSchema,
    });
    
    const { control, reset, getValues, setValue, watch, handleSubmit } = useForm<AddressFormData>({
        resolver: yupResolver(AddressFormSchema),
        defaultValues: {
            ...props.address,
            [ClientAddressFields.CountryCode]: 1
        },
        mode: 'onBlur'
    });

    const [isSaving, setSaving] = useState<boolean>(false)
    const [provinces, setProvinces] = useState<Province[] | null>(null);
    const [partidos, setPartidos] = useState<Department[] | null>(null);
    const [filteredPartidos, setFilteredPartidos] = useState<Department[] | null>(null)
    const [localities, setLocalities] = useState<Municipality[] | null>(null);
    const [filteredlocalities, setFilteredLocalities] = useState<Municipality[] | null>(null);
    const watchPais = useWatch({control, name: ClientAddressFields.CountryCode});
    const watchCodigoPostal = useWatch({control, name: ClientAddressFields.PostalCode});
    const watchProvincia = useWatch({control, name: ClientAddressFields.ProvinceCode});
    const watchPartido = useWatch({control, name: ClientAddressFields.DepartmentCode});
    
    useEffect(() => {
        props.address && reset({
            ...props.address,
            [ClientAddressFields.CountryCode]: 1
        })
    }, [props.address])
    
    const handleClose = () => {
        reset({
            [ClientAddressFields.AddressTypeCode]: props.addressType,
            [ClientAddressFields.CountryCode]: 1,
            [ClientAddressFields.Verified]: true,
            [ClientAddressFields.Street]: '',
            [ClientAddressFields.StreetNumber]: '',
            [ClientAddressFields.Floor]: '',
            [ClientAddressFields.Apartment]: '',
            [ClientAddressFields.PostalCode]: '',
            [ClientAddressFields.ProvinceCode]: null,
            [ClientAddressFields.DepartmentCode]: null,
            [ClientAddressFields.MunicipalityCode]: null,
        })
        props.onClose()
    }

    const onSubmit = (data: AddressFormData) => {
        setSaving(true);
        const addressToInsert: AddressFormData = {
            ...data,
            [ClientAddressFields.AddressTypeCode]: props.addressType,
            [ClientAddressFields.CountryCode]: 1,
            [ClientAddressFields.Verified]: true
        }
        
        !props.address
            ? HttpClientAddress.insert(props.clientId, addressToInsert as ClientAddress).then(() => {
                setSaving(false);
                handleClose();
                props.onSubmitDialog();
            })
            : HttpClientAddress.update(props.clientId, addressToInsert as ClientAddress).then(() => {
                setSaving(false);
                handleClose();
                props.onSubmitDialog();
            });
    }

    const loadAddressData = useCallback(async (CP: string) => {
        if (CP) {
            const data = await HttpClientAddressData.getAddressDataByPostalCodeAndCountry(1, CP);
            const unique_provinces = removeDuplicatesById(data.lstProvincias) as Province[];
            const unique_partidos = removeDuplicatesById(data.lstPartidos) as Department[];
            const unique_localities = removeDuplicatesById(data.lstLocalidades) as Municipality[];
            reset({
                ...getValues(),
                [ClientAddressFields.ProvinceCode]: 0,
                [ClientAddressFields.DepartmentCode]: null,
                [ClientAddressFields.MunicipalityCode]: null,
            })
            setProvinces(unique_provinces);
            setPartidos(unique_partidos);
            setLocalities(unique_localities);
        }
    }, [watchPais, watchCodigoPostal]);

    const loadFilteredPartidos = () => {
        setFilteredPartidos(partidos?.filter(p => p.intCodProvincia === watchProvincia) || null)
    }

    const loadFilteredLocalities = () => {
        if (watchPartido) {
            setFilteredLocalities(localities?.filter(l => l.intCodProvincia === watchProvincia
                && l.intCodPartido === watchPartido) || null)
        } else {
            setFilteredLocalities(localities?.filter(l => l.intCodProvincia === watchProvincia) || null)
        }
    }

    useEffect(() => {
        loadFilteredPartidos();
    }, [partidos, watchProvincia])

    useEffect(() => {
        loadFilteredLocalities();
    }, [localities, watchPartido, watchProvincia])

    useEffect(() => {
        if (provinces?.length === 1) {
            setValue(ClientAddressFields.ProvinceCode, provinces[0].id)
        }
    }, [provinces])

    useEffect(() => {
        if (filteredPartidos?.length === 1) {
            setValue(ClientAddressFields.DepartmentCode, filteredPartidos[0].id)
        }
    }, [filteredPartidos])

    useEffect(() => {
        if (filteredlocalities?.length === 1) {
            setValue(ClientAddressFields.MunicipalityCode, filteredlocalities[0].id)
        }
    }, [filteredlocalities])

    useEffect(() => {
        (async () => {
            if (watch(ClientAddressFields.CountryCode) !== null) {
                await loadAddressData(watch(ClientAddressFields.PostalCode) || '');
            }
        })();
    }, [watchPais, props.open])

    const onBlurCP = useCallback(() => {
        if (watch(ClientAddressFields.CountryCode) !== null) {
            loadAddressData(watch(ClientAddressFields.PostalCode) || '');
        }

    },[watchPais, watchCodigoPostal])

    useEffect(() => {
        (async () => {
            if (props.address) {
                await loadAddressData(props.address?.[ClientAddressFields.PostalCode]);
            }
        })();
    }, [])

    return (
        <Dialog open={props.open} onClose={handleClose} maxWidth={'sm'} fullWidth>
            <BaseDialogTitle title={props.renderAddressTitle()} onClose={handleClose} />
            <Divider />
            <DialogContent>
                <Grid container justifyContent={'center'} spacing={4}>
                    <Grid item xs={7.5} justifyContent={'center'}>
                        <ControlledTextField
                            name={ClientAddressFields.Street}
                            label='Calle'
                            control={control}
                            required
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={2.5}>
                        <ControlledTextField
                            name={ClientAddressFields.StreetNumber}
                            label='Número'
                            control={control}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <ControlledTextField
                            name={ClientAddressFields.Floor}
                            label='Piso'
                            control={control}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <ControlledTextField control={control}
                                             name={ClientAddressFields.Apartment}
                                             label='Departamento'
                                             fullWidth
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <ControlledTextField control={control}
                                             onBlur={onBlurCP}
                                             name={ClientAddressFields.PostalCode}
                                             label='Cód. Postal'
                                             fullWidth
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <ControlledTextField select
                                             options={provinces || []}
                                             control={control}
                                             name={ClientAddressFields.ProvinceCode}
                                             label='Provincia'
                                             disabled
                                             fullWidth
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledTextField select
                                             options={filteredPartidos || []}
                                             disabled={!filteredPartidos || filteredPartidos.length <= 1}
                                             control={control}
                                             name={ClientAddressFields.DepartmentCode}
                                             label='Departmento'
                                             fullWidth
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledTextField select
                                             options={filteredlocalities || []}
                                             disabled={!filteredlocalities || filteredlocalities.length <= 1}
                                             control={control}
                                             name={ClientAddressFields.MunicipalityCode}
                                             label='Localidad'
                                             fullWidth
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button color={"primary"} variant={"contained"} onClick={handleSubmit(onSubmit)}>Guardar</Button>
            </DialogActions>
            {isSaving && <LoaderBlockUI/>}
        </Dialog>
    )
}

export default DialogAddress